import React, { useEffect } from 'react'
import { TextField, TextFieldProps } from '@mui/material'
import { TimePicker, TimePickerProps } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import TextInput from './TextInput'

function parseTime(timeValue: string | dayjs.Dayjs | null) {
  // If value is null, return null
  if (timeValue === null) {
    return null
  }

  // If value is already a dayjs object, return it
  if (dayjs.isDayjs(timeValue)) {
    return timeValue
  }

  // If value is a string, parse it
  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const [hours, minutes] = timeValue.split(':').map(Number)
    return dayjs().hour(hours).minute(minutes)
  }

  // Default to current time if value is invalid
  return dayjs()
}

export type TimePickerInputProps = Pick<TimePickerProps<dayjs.Dayjs, dayjs.Dayjs>, 'ampm' | 'views' | 'inputFormat' | 'mask'> & {
  label?: string
  value: string | dayjs.Dayjs | null
  onChange: (value: dayjs.Dayjs | null, inputValue?: string) => void
  error?: boolean
  helperText?: string | React.ReactNode
  fullWidth?: boolean
  readOnly?: boolean
  disabled?: boolean
  options?: any // Add options prop for compatibility
  name?: string // Add name prop for compatibility with form fields
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void // Add onBlur for form handling
}

export default function TimePickerInput({
  label,
  value,
  onChange,
  error,
  helperText,
  fullWidth = false,
  readOnly = false,
  disabled = false,
  onBlur,
  ...rest
}: TimePickerInputProps) {
  // Parse the time string (HH:MM) to a dayjs object or handle dayjs object directly

  const handleChange = (newValue: dayjs.Dayjs | null) => {
    if (readOnly || disabled) return

    onChange(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        {...rest}
        ampm={false}
        label={label}
        value={parseTime(value)}
        onChange={handleChange}
        readOnly={readOnly}
        disabled={disabled}
        renderInput={(params: TextFieldProps) => (
          <TextInput
            {...params}
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
            onBlur={onBlur}
            sx={{
              '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
                marginRight: 0,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  )
}
